import React from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import ClockIcon from '@atlaskit/icon/core/clock';
import PauseIcon from '@atlaskit/icon/glyph/vid-pause';
import CheckMarkIcon from '@atlaskit/icon/utility/check-mark';
import CrossIcon from '@atlaskit/icon/utility/cross';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { NEW_SLA_FORMAT } from '../../common/constants';
import { COMPLETED, PAUSED, type SlaStatus, type TransformedGoalView } from '../../common/types';

const SEMIBOLD = 600;

const SUCCESS_STYLE = {
	backgroundColor: token('color.background.neutral', colors.N20),
	textColor: token('color.text.subtle', colors.N500),
	iconColor: token('color.icon.success', colors.G300),
	fontWeight: 'bold',
	backgroundIconColor: token('color.background.neutral.subtle', 'transparent'),
} as const;

const FAILURE_STYLE = {
	backgroundColor: token('color.background.neutral', colors.N20),
	textColor: token('color.text.subtle', colors.N500),
	iconColor: token('color.icon.danger', colors.R300),
	fontWeight: 'bold',
	backgroundIconColor: token('color.background.neutral.subtle', 'transparent'),
} as const;

const PAUSED_STYLE = {
	backgroundColor: token('color.background.neutral.subtle', 'transparent'),
	textColor: token('color.text.subtle', colors.N500),
	iconColor: token('color.icon.subtle', colors.N500),
	fontWeight: SEMIBOLD,
	backgroundIconColor: token('color.background.neutral', colors.N20),
} as const;

const PAUSED_BREACHED_STYLE = {
	backgroundColor: token('color.background.neutral.subtle', 'transparent'),
	textColor: token('color.text.subtle', colors.N500),
	fontWeight: SEMIBOLD,
	iconColor: token('color.icon.inverse', colors.N0),
	backgroundIconColor: token('color.background.danger.bold', colors.R300),
} as const;

const PAUSED_UNDER_THIRTY_REMAINING_STYLE = {
	backgroundColor: token('color.background.neutral.subtle', 'transparent'),
	textColor: token('color.text.subtle', colors.N500),
	fontWeight: SEMIBOLD,
	iconColor: token('color.icon.warning.inverse', colors.N0),
	backgroundIconColor: token('color.background.warning.bold', colors.Y400),
} as const;

const ONGOING_STYLE = {
	backgroundColor: token('color.background.neutral.subtle', 'transparent'),
	textColor: token('color.text.subtle', colors.N500),
	iconColor: token('color.icon.subtle', colors.N70),
	fontWeight: SEMIBOLD,
	backgroundIconColor: token('color.background.neutral.subtle', 'transparent'),
} as const;

const ONGOING_BREACHED_STYLE = {
	backgroundColor: token('color.background.danger.bold', colors.R300),
	textColor: token('color.text.inverse', colors.N0),
	iconColor: token('color.icon.inverse', colors.N0),
	fontWeight: 'bold',
	backgroundIconColor: token('color.background.neutral.subtle', 'transparent'),
} as const;

const ONGOING_UNDER_THIRTY_REMAINING_STYLE = {
	backgroundColor: token('color.background.warning.bold', colors.Y400),
	textColor: token('color.text.warning.inverse', colors.N0),
	iconColor: token('color.icon.warning.inverse', colors.N0),
	fontWeight: 'bold',
	backgroundIconColor: token('color.background.neutral.subtle', 'transparent'),
} as const;

const getStyles = (isBreached: boolean, status: SlaStatus, timeRemainingInMinutes: number) => {
	if (status === COMPLETED && !isBreached) {
		return SUCCESS_STYLE;
	}
	if (status === COMPLETED && isBreached) {
		return FAILURE_STYLE;
	}
	if (status === PAUSED) {
		if (timeRemainingInMinutes < 0) {
			return PAUSED_BREACHED_STYLE;
		}
		if (timeRemainingInMinutes < 30) {
			return PAUSED_UNDER_THIRTY_REMAINING_STYLE;
		}
		return PAUSED_STYLE;
	}
	if (timeRemainingInMinutes < 0) {
		return ONGOING_BREACHED_STYLE;
	}
	if (timeRemainingInMinutes < 30) {
		return ONGOING_UNDER_THIRTY_REMAINING_STYLE;
	}
	return ONGOING_STYLE;
};

const getIcon = (
	isBreached: boolean,
	status: SlaStatus,
	iconColor: string,
	backgroundIconColor: string,
) => {
	switch (status) {
		case COMPLETED: {
			const TickOrCrossIcon = isBreached ? CrossIcon : CheckMarkIcon;
			return (
				<IconContainer iconColor={iconColor}>
					{}
					{/* @ts-expect-error - TS2769 - No overload matches this call. */}
					<TickOrCrossIcon size="large" />
				</IconContainer>
			);
		}
		case PAUSED: {
			return (
				<PauseIconContainer iconColor={iconColor} backgroundIconColor={backgroundIconColor}>
					<PauseIcon label="pauseIcon" size="small" />
				</PauseIconContainer>
			);
		}
		default:
			return (
				<IconContainer iconColor={iconColor}>
					{}
					<ClockIcon label="" LEGACY_size="large" color="currentColor" />
				</IconContainer>
			);
	}
};

type Props = {
	value: TransformedGoalView;
};

export const sendAnalyticsEvent = (analyticsEvent: UIAnalyticsEvent) =>
	fireUIAnalytics(analyticsEvent, 'slaWidgetHovered');

const Sla = ({
	value: {
		isBreached,
		timeRemainingFriendly,
		status,
		timeRemainingInMinutes,
		hoverContent,
		slaDisplayFormat,
	},
}: Props) => {
	if (timeRemainingFriendly === null) return null;

	const styles = getStyles(isBreached, status, timeRemainingInMinutes);

	const sla = (
		<LozengeContainer backgroundColor={styles.backgroundColor}>
			<TextContainer textColor={styles.textColor} fontWeight={styles.fontWeight}>
				{timeRemainingFriendly}
			</TextContainer>
			{getIcon(isBreached, status, styles.iconColor, styles.backgroundIconColor)}
		</LozengeContainer>
	);

	return slaDisplayFormat === NEW_SLA_FORMAT ? (
		<Tooltip content={hoverContent || ''} truncate onShow={sendAnalyticsEvent}>
			{sla}
		</Tooltip>
	) : (
		sla
	);
};

export default Sla;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeContainer = styled.div<{ backgroundColor: string }>({
	display: 'flex',
	borderRadius: token('space.200'),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props: any) => props.backgroundColor,
	height: token('space.400'),
	alignItems: 'center',
	justifycontent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled.div<{
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	fontWeight: any;
	textColor: string;
}>({
	flex: '1 1 auto',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	fontSize: token('space.150', '12px'),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	fontWeight: (props: any) => props.fontWeight,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props: any) => props.textColor,
	textAlign: 'right',
	paddingLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconContainer = styled.div<{ iconColor: string }>({
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props: any) => props.iconColor,
	width: token('space.400'),
	height: token('space.300'),
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PauseIconContainer = styled.div<{
	iconColor: string;
	backgroundIconColor: string;
}>({
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props: any) => props.iconColor,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props: any) => props.backgroundIconColor,
	borderRadius: token('space.025', '2px'),
	height: token('space.300'),
	width: token('space.300'),
	margin: `${token('space.025', '2px')} ${token('space.025', '2px')} ${token('space.025', '2px')} 3px`,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});
